import { reactive } from "vue";
import { defineNuxtPlugin } from "#app";
export var Breakpoint = /* @__PURE__ */ ((Breakpoint2) => {
  Breakpoint2["Xs"] = "xs";
  Breakpoint2["Sm"] = "sm";
  Breakpoint2["Md"] = "md";
  Breakpoint2["Lg"] = "lg";
  Breakpoint2["Xl"] = "xl";
  return Breakpoint2;
})(Breakpoint || {});
export var BreakpointWidth = /* @__PURE__ */ ((BreakpointWidth2) => {
  BreakpointWidth2[BreakpointWidth2["Xs"] = 0] = "Xs";
  BreakpointWidth2[BreakpointWidth2["Sm"] = 640] = "Sm";
  BreakpointWidth2[BreakpointWidth2["Md"] = 768] = "Md";
  BreakpointWidth2[BreakpointWidth2["Lg"] = 1024] = "Lg";
  BreakpointWidth2[BreakpointWidth2["Xl"] = 1280] = "Xl";
  return BreakpointWidth2;
})(BreakpointWidth || {});
export function getBreakPointByWidth(width) {
  if (width >= 1280 /* Xl */) {
    return "xl" /* Xl */;
  }
  if (width >= 1024 /* Lg */) {
    return "lg" /* Lg */;
  }
  if (width >= 768 /* Md */) {
    return "md" /* Md */;
  }
  if (width >= 640 /* Sm */) {
    return "sm" /* Sm */;
  }
  return "xs" /* Xs */;
}
export function getDefaultWidthByBreakpoint(breakpoint) {
  switch (breakpoint) {
    case "xl" /* Xl */:
      return 1920;
    case "lg" /* Lg */:
      return 1280 /* Xl */;
    case "md" /* Md */:
      return 1024 /* Lg */;
    case "sm" /* Sm */:
      return 768 /* Md */;
  }
  return 480;
}
class BreakpointService {
  defaultWidth;
  defaultHeight;
  xs;
  sm;
  md;
  lg;
  xl;
  currentWidth = 0;
  currentHeight = 0;
  current = "xs" /* Xs */;
  smAndDown = false;
  smAndUp = false;
  mdAndDown = false;
  mdAndUp = false;
  lgAndDown = false;
  lgAndUp = false;
  xsOnly = false;
  smOnly = false;
  mdOnly = false;
  lgOnly = false;
  xlOnly = false;
  resizeTimeout;
  constructor(defaultScreenWidth = 0, defaultScreenHeight = 0) {
    this.defaultWidth = defaultScreenWidth;
    this.defaultHeight = defaultScreenHeight;
    this.xs = 0 /* Xs */;
    this.sm = 640 /* Sm */;
    this.md = 768 /* Md */;
    this.lg = 1024 /* Lg */;
    this.xl = 1280 /* Xl */;
    this.init();
  }
  init() {
    this.update();
    if (typeof window === "undefined") {
      return;
    }
    window.addEventListener("resize", this.onResize.bind(this), { passive: true });
  }
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(this.update.bind(this), 200);
  }
  update() {
    const height = this.getClientHeight();
    const width = this.getClientWidth();
    const scrollBarWidth = 10;
    this.xsOnly = width < this.sm;
    this.smOnly = width < this.md && !this.xsOnly;
    this.mdOnly = width < this.lg && !(this.smOnly || this.xsOnly);
    this.lgOnly = width < this.xl && !(this.mdOnly || this.smOnly || this.xsOnly);
    this.xlOnly = width >= this.xl - scrollBarWidth;
    this.smAndDown = (this.xsOnly || this.smOnly) && !(this.mdOnly || this.lgOnly || this.xlOnly);
    this.smAndUp = !this.xsOnly && (this.smOnly || this.mdOnly || this.lgOnly || this.xlOnly);
    this.mdAndDown = (this.xsOnly || this.smOnly || this.mdOnly) && !(this.lgOnly || this.xlOnly);
    this.mdAndUp = !(this.xsOnly || this.smOnly) && (this.mdOnly || this.lgOnly || this.xlOnly);
    this.lgAndDown = (this.xsOnly || this.smOnly || this.mdOnly || this.lgOnly) && !this.xlOnly;
    this.lgAndUp = !(this.xsOnly || this.smOnly || this.mdOnly) && (this.lgOnly || this.xlOnly);
    this.currentWidth = width;
    this.currentHeight = height;
    if (this.xlOnly) {
      this.current = "xl" /* Xl */;
    } else if (this.lgOnly) {
      this.current = "lg" /* Lg */;
    } else if (this.mdOnly) {
      this.current = "md" /* Md */;
    } else if (this.smOnly) {
      this.current = "sm" /* Sm */;
    } else {
      this.current = "xs" /* Xs */;
    }
  }
  isSameOrDown(breakpoint) {
    switch (breakpoint) {
      case "xs" /* Xs */:
        return this.xsOnly;
      case "sm" /* Sm */:
        return this.smAndDown;
      case "md" /* Md */:
        return this.mdAndDown;
      case "lg" /* Lg */:
        return this.lgAndDown;
    }
    return true;
  }
  isSameOrUp(breakpoint) {
    switch (breakpoint) {
      case "sm" /* Sm */:
        return this.smAndUp;
      case "md" /* Md */:
        return this.mdAndUp;
      case "lg" /* Lg */:
        return this.lgAndUp;
      case "xl" /* Xl */:
        return this.xlOnly;
    }
    return true;
  }
  // Cross-browser support as described in:
  // https://stackoverflow.com/questions/1248081
  getClientWidth() {
    if (typeof document === "undefined") {
      return this.defaultWidth;
    }
    return Math.max(document.documentElement.clientWidth, window.innerWidth || this.defaultWidth);
  }
  getClientHeight() {
    if (typeof document === "undefined") {
      return this.defaultHeight;
    }
    return Math.max(document.documentElement.clientHeight, window.innerHeight || this.defaultHeight);
  }
}
export default defineNuxtPlugin((nuxtApp) => {
  let defaultScreenWidth = 375;
  let defaultScreenHeight = 375 / 0.5625;
  if (nuxtApp.$device.isTablet) {
    defaultScreenWidth = 768;
    defaultScreenHeight = 768 / 0.625;
  } else if (nuxtApp.$device.isDesktop) {
    defaultScreenWidth = 1280;
    defaultScreenHeight = 1280 * 0.5625;
  }
  return {
    provide: {
      breakpoint: reactive(new BreakpointService(defaultScreenWidth, defaultScreenHeight))
    }
  };
});
